import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { C, fmtINR } from "./ipd-billing/constants";
import SvgIcon from "./ipd-billing/SvgIcon";
import { getManagerDashboard, toBillingError } from "./ipd-billing/billing-api";
import { useBillingPermissions } from "./ipd-billing/useBillingPermissions";
import { IPD_COLORS } from "./ipd/utils/ipdColors";

// ─────────────────────────────────────────────────────────────────────────────
// Billing Manager Dashboard (/billing-manager-dashboard)
//
// What the manager signed off and what it actually did:
//   • bills cancelled — and the services deleted off them afterwards
//   • refunds and cancelled-receipt-to-deposit transfers
//   • adhoc discounts above the auto-approve limit, approved and rejected
//
// Everything shown here already took effect: cancellations and refunds count only
// once EXECUTED, discounts once APPROVED. The API is manager-only (role level 3) and
// refuses anyone below it, so the screen shows that refusal rather than an empty page.
// ─────────────────────────────────────────────────────────────────────────────

const DATE_PRESETS = [
  { id: "today", label: "Today" },
  { id: "7d", label: "Last 7 days" },
  { id: "month", label: "This month" },
  { id: "all", label: "All" },
];

const TABS = [
  {
    id: "cancelled",
    label: "Bills cancelled",
    title: "Cancelled bills",
    subtitle: "Executed cancellations, with the services deleted from the bill afterwards",
    empty: "No cancelled bills in this period.",
    badge: "muted",
  },
  {
    id: "services",
    label: "Services removed",
    title: "Services removed",
    subtitle: "Line items added to or deleted from a bill after it was cancelled",
    empty: "No services removed after cancellation in this period.",
    badge: "danger",
    tone: IPD_COLORS.error.text,
  },
  {
    id: "refunds",
    label: "Refunds",
    title: "Refunds",
    subtitle: "Executed refunds and cancelled-receipt transfers to deposit",
    empty: "No refunds in this period.",
    badge: "info",
  },
  {
    id: "approved",
    label: "Discounts approved",
    title: "Discounts you approved",
    subtitle: "Adhoc discounts above the auto-approve limit",
    empty: "No approved discounts in this period.",
    badge: "success",
    tone: IPD_COLORS.success.text,
  },
  {
    id: "rejected",
    label: "Discounts rejected",
    title: "Discounts rejected",
    subtitle: "Refused — these never reached a bill",
    empty: "No rejected discounts in this period.",
    badge: "danger",
  },
];

const BADGE = {
  muted: { bg: "#F1F5F9", fg: C.textSub, br: C.border },
  info: { bg: IPD_COLORS.info.bg, fg: IPD_COLORS.info.text, br: IPD_COLORS.info.border },
  success: { bg: IPD_COLORS.success.bg, fg: IPD_COLORS.success.text, br: IPD_COLORS.success.border },
  danger: { bg: IPD_COLORS.error.bg, fg: IPD_COLORS.error.text, br: IPD_COLORS.error.border },
};

const dateMs = (value) => {
  if (!value) return 0;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const fmtWhen = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? String(value)
    : d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const sumAmount = (rows) => rows.reduce((n, r) => n + (Number(r.amount) || 0), 0);

const inDateRange = (value, range) => {
  if (range === "all") return true;
  if (!value) return true;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return true;
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (range === "today") return d >= startOfToday;
  if (range === "7d") {
    const start = new Date(startOfToday);
    start.setDate(start.getDate() - 6);
    return d >= start;
  }
  if (range === "month") return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  return true;
};

const haystack = (parts) => parts.filter((p) => p != null && p !== "").join(" ").toLowerCase();

const rowMatches = (row, q) => {
  if (!q) return true;
  return haystack([
    row.patientName,
    row.admissionId,
    row.requestNumber,
    row.invoiceNumber,
    row.receiptNumber,
    row.reason,
    row.statusLabel,
    ...(row.cancelledCharges || []).flatMap((c) => [c.serviceName, c.category, c.serviceCode]),
  ]).includes(q);
};

const serviceMatches = (row, q) => {
  if (!q) return true;
  return haystack([
    row.serviceName,
    row.category,
    row.serviceCode,
    row.patientName,
    row.admissionId,
    row.invoiceNumber,
    row.receiptNumber,
  ]).includes(q);
};

const groupByAdmission = (rows) => {
  const buckets = new Map();
  const order = [];
  rows.forEach((row) => {
    const key = row.admissionId == null || row.admissionId === "" ? `row:${row.id}` : `adm:${row.admissionId}`;
    if (!buckets.has(key)) {
      buckets.set(key, []);
      order.push(key);
    }
    buckets.get(key).push(row);
  });
  return order
    .map((key) => {
      const items = buckets.get(key).slice().sort((a, b) => dateMs(b.requestedAt) - dateMs(a.requestedAt));
      return {
        key,
        items,
        isGroup: key.startsWith("adm:") && items.length > 1,
        admissionId: items[0].admissionId,
        patientName: items[0].patientName,
      };
    })
    .sort((a, b) => dateMs(b.items[0].requestedAt) - dateMs(a.items[0].requestedAt));
};

function StatCard({ label, value, sub, tone, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      title={`View ${label.toLowerCase()}`}
      className="bmd-stat"
      style={{
        flex: "1 1 170px",
        minWidth: "170px",
        textAlign: "left",
        background: selected ? IPD_COLORS.selectedBg : C.card,
        border: `2px solid ${selected ? C.primary : C.border}`,
        borderRadius: "12px",
        padding: "12px 16px",
        cursor: "pointer",
        fontFamily: "inherit",
      }}
    >
      <div style={{ fontSize: "11px", fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      <div style={{ fontSize: "22px", fontWeight: 800, color: tone || C.text, marginTop: "6px" }}>{value}</div>
      {sub != null && <div style={{ fontSize: "12px", color: C.textSub, marginTop: "2px" }}>{sub}</div>}
    </button>
  );
}

function StatusBadge({ label, tone }) {
  const t = BADGE[tone] || BADGE.muted;
  return (
    <span style={{
      display: "inline-block",
      padding: "3px 8px",
      borderRadius: "999px",
      background: t.bg,
      color: t.fg,
      border: `1px solid ${t.br}`,
      fontSize: "11px",
      fontWeight: 700,
      whiteSpace: "nowrap",
    }}>
      {label}
    </span>
  );
}

function EmptyRow({ text }) {
  return <div style={{ padding: "28px 18px", fontSize: "13px", color: C.textSub, textAlign: "center" }}>{text}</div>;
}

function PatientCell({ row }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: "13px", fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis" }}>
        {row.patientName || "—"}
      </div>
      {row.admissionId ? (
        <div style={{ fontSize: "12px", color: C.textSub, marginTop: "2px" }}>Admission {row.admissionId}</div>
      ) : null}
    </div>
  );
}

function RefsCell({ row }) {
  return (
    <div style={{ minWidth: 0, fontSize: "12px", lineHeight: 1.45 }}>
      {row.invoiceNumber ? <div style={{ fontWeight: 600, color: C.text }}>{row.invoiceNumber}</div> : null}
      {row.receiptNumber ? <div style={{ color: C.textSub }}>{row.receiptNumber}</div> : null}
      {row.requestNumber ? <div style={{ color: IPD_COLORS.textMuted, wordBreak: "break-all" }}>{row.requestNumber}</div> : null}
      {!row.invoiceNumber && !row.receiptNumber && !row.requestNumber ? <span style={{ color: C.textSub }}>—</span> : null}
    </div>
  );
}

function ChargesExpand({ row, open, onToggle }) {
  const n = row.cancelledCharges.length;
  if (!n) return null;
  return (
    <>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          marginTop: "6px",
          padding: "3px 8px",
          background: IPD_COLORS.error.bg,
          border: `1px solid ${IPD_COLORS.error.border}`,
          borderRadius: "6px",
          fontSize: "11px",
          fontWeight: 700,
          color: C.danger,
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        {n} {n === 1 ? "service" : "services"} · {fmtINR(row.cancelledChargesTotal)}
        <SvgIcon name={open ? "chevron-up" : "chevron-down"} size={12} color={C.danger} />
      </button>
      {open && (
        <div style={{ marginTop: "8px", background: "#f8f9fa", borderRadius: "8px", padding: "8px 10px" }}>
          {row.cancelledCharges.map((c) => (
            <div key={c.id} style={{ display: "flex", justifyContent: "space-between", gap: "10px", fontSize: "12px", color: C.text, padding: "2px 0" }}>
              <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {c.serviceName}
                {c.category ? <span style={{ color: C.textSub }}>{` · ${c.category}`}</span> : null}
              </span>
              <span style={{ color: C.danger, fontWeight: 700, flexShrink: 0 }}>– {fmtINR(c.amount)}</span>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", borderTop: `1px solid ${C.border}`, marginTop: "6px", paddingTop: "6px", fontSize: "12px", fontWeight: 800 }}>
            <span>Removed from bill</span>
            <span style={{ color: C.danger }}>– {fmtINR(row.cancelledChargesTotal)}</span>
          </div>
        </div>
      )}
    </>
  );
}

const th = {
  fontSize: "11px",
  fontWeight: 700,
  color: C.textSub,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  padding: "10px 14px",
  background: "#F8FAFC",
  borderBottom: `1px solid ${C.border}`,
  textAlign: "left",
  whiteSpace: "nowrap",
};
const td = { padding: "12px 14px", borderBottom: `1px solid ${C.border}`, verticalAlign: "top" };

function ApprovalTable({ rows, badge, empty, expanded, onToggle }) {
  if (!rows.length) return <EmptyRow text={empty} />;
  const groups = groupByAdmission(rows);
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "760px" }}>
        <thead>
          <tr>
            <th style={th}>Patient</th>
            <th style={th}>Invoice / receipt</th>
            <th style={{ ...th, textAlign: "right" }}>Amount</th>
            <th style={th}>Reason</th>
            <th style={th}>When</th>
            <th style={{ ...th, textAlign: "right" }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((g) => (
            <React.Fragment key={g.key}>
              {g.isGroup && (
                <tr>
                  <td colSpan={6} style={{ padding: "8px 14px", background: "#F8FAFC", borderBottom: `1px solid ${C.border}`, fontSize: "12px", fontWeight: 700, color: C.text }}>
                    {g.patientName || "—"}
                    <span style={{ color: C.textSub, fontWeight: 600 }}>{` · Admission ${g.admissionId} · ${g.items.length} items · ${fmtINR(sumAmount(g.items))}`}</span>
                  </td>
                </tr>
              )}
              {g.items.map((row) => (
                <tr key={row.id} className="bmd-row">
                  <td style={td}>
                    <PatientCell row={row} />
                    <ChargesExpand row={row} open={Boolean(expanded[row.id])} onToggle={() => onToggle(row.id)} />
                  </td>
                  <td style={td}><RefsCell row={row} /></td>
                  <td style={{ ...td, textAlign: "right", fontSize: "14px", fontWeight: 800, color: C.text, whiteSpace: "nowrap" }}>{fmtINR(row.amount)}</td>
                  <td style={{ ...td, fontSize: "12px", color: C.text, maxWidth: "240px" }}>{row.reason || "—"}</td>
                  <td style={{ ...td, fontSize: "12px", color: C.textSub, whiteSpace: "nowrap" }}>{fmtWhen(row.requestedAt)}</td>
                  <td style={{ ...td, textAlign: "right" }}><StatusBadge label={row.statusLabel} tone={badge} /></td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ServiceTable({ rows, empty }) {
  if (!rows.length) return <EmptyRow text={empty} />;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "720px" }}>
        <thead>
          <tr>
            <th style={th}>Change</th>
            <th style={th}>Service</th>
            <th style={th}>Patient</th>
            <th style={th}>Invoice / receipt</th>
            <th style={{ ...th, textAlign: "right" }}>Amount</th>
            <th style={th}>When</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="bmd-row">
              <td style={td}>
                <StatusBadge tone={row.action === "ADDED" ? "success" : "danger"}
                             label={row.action === "ADDED" ? "Added" : "Removed"} />
              </td>
              <td style={td}>
                <div style={{ fontSize: "13px", fontWeight: 700, color: C.text }}>{row.serviceName}</div>
                {row.category ? <div style={{ fontSize: "12px", color: C.textSub, marginTop: "2px" }}>{row.category}</div> : null}
              </td>
              <td style={td}><PatientCell row={row} /></td>
              <td style={td}><RefsCell row={row} /></td>
              <td style={{ ...td, textAlign: "right", fontSize: "14px", fontWeight: 800,
                           color: row.action === "ADDED" ? IPD_COLORS.success.text : C.danger, whiteSpace: "nowrap" }}>
                {row.action === "ADDED" ? "+" : "–"} {fmtINR(row.amount)}
              </td>
              <td style={{ ...td, fontSize: "12px", color: C.textSub, whiteSpace: "nowrap" }}>{fmtWhen(row.requestedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function BillingManagerDashboard() {
  const history = useHistory();
  const { meetsLevel, loaded: permsLoaded } = useBillingPermissions();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("cancelled");
  const [range, setRange] = useState("all");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await getManagerDashboard());
    } catch (err) {
      setError(toBillingError(err, "Could not load the dashboard"));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setExpanded({}); }, [data]);

  const notManager = permsLoaded && !meetsLevel(3);
  const q = query.trim().toLowerCase();
  const active = TABS.find((t) => t.id === tab) || TABS[0];

  const dated = useMemo(() => {
    if (!data) return { cancelled: [], refunds: [], approved: [], rejected: [], services: [] };
    const byDate = (rows) => rows.filter((r) => inDateRange(r.requestedAt, range));
    const cancelled = byDate(data.billCancellations);
    return {
      cancelled,
      refunds: byDate(data.refunds),
      approved: byDate(data.approvedDiscounts),
      rejected: byDate(data.rejectedDiscounts),
      // Straight from the API. This used to be flattened out of each cancellation row,
      // which only ever saw removals — it missed additions entirely, and missed whole
      // admissions whose bill was cancelled outside the approval workflow.
      services: byDate(data.serviceChanges || []),
    };
  }, [data, range]);

  const kpis = useMemo(() => ({
    cancelled: { count: dated.cancelled.length, amount: sumAmount(dated.cancelled) },
    // The tile says "Services removed", so it counts removals only — additions are
    // listed underneath but must not inflate a figure labelled as removals.
    services: {
      count: dated.services.filter((r) => r.action !== "ADDED").length,
      amount: sumAmount(dated.services.filter((r) => r.action !== "ADDED")),
    },
    refunds: { count: dated.refunds.length, amount: sumAmount(dated.refunds) },
    approved: { count: dated.approved.length, amount: sumAmount(dated.approved) },
    rejected: { count: dated.rejected.length, amount: sumAmount(dated.rejected) },
  }), [dated]);

  const visible = useMemo(() => {
    const src = dated[tab] || [];
    const match = tab === "services" ? serviceMatches : rowMatches;
    return src.filter((row) => match(row, q));
  }, [dated, tab, q]);

  const toggleExpanded = (id) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div style={{ minHeight: "calc(100vh - 140px)", background: C.bg }}>
      <style>{`
        @keyframes ipdFadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
        .bmd-row:hover td { background: #F8FAFC; }
        .bmd-stat:hover { border-color: ${C.primary} !important; }
        .bmd-stat:focus-visible, .bmd-pill:focus-visible { outline: 2px solid ${C.primary}; outline-offset: 2px; }
      `}</style>

      <div style={{ display: "flex", alignItems: "center", gap: "14px", padding: "18px 32px", background: C.card, borderBottom: `1px solid ${C.border}` }}>
        <button
          type="button"
          onClick={() => history.push("/main-landing")}
          style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 12px", background: "#fff", border: `1px solid ${C.border}`, borderRadius: "8px", color: C.text, fontSize: "13px", fontWeight: 600, cursor: "pointer" }}
        >
          <SvgIcon name="back" size={16} color={C.text} />
          Home
        </button>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <div style={{ width: "34px", height: "34px", borderRadius: "9px", background: C.primary, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <SvgIcon name="invoice" size={18} color="#fff" />
          </div>
          <div>
            <div style={{ fontSize: "16px", fontWeight: 800, color: C.text }}>Billing Manager Dashboard</div>
            <div style={{ fontSize: "12px", color: C.textSub }}>
              Approved cancellations, refunds and discounts{data?.roleName ? ` · ${data.roleName}` : ""}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          style={{ marginLeft: "auto", padding: "7px 14px", background: "#fff", border: `1px solid ${C.border}`, borderRadius: "8px", color: C.text, fontSize: "13px", fontWeight: 600, cursor: loading ? "not-allowed" : "pointer" }}
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div style={{ padding: "20px 32px", animation: "ipdFadeIn 0.2s ease" }}>
        {notManager && (
          <div style={{ padding: "16px 18px", background: "rgba(220,53,69,0.08)", border: `1px solid ${C.border}`, borderRadius: "10px", fontSize: "13px", color: C.danger, fontWeight: 600 }}>
            This dashboard is available to billing managers only.
          </div>
        )}
        {!notManager && error && (
          <div style={{ padding: "16px 18px", background: "rgba(220,53,69,0.08)", border: `1px solid ${C.border}`, borderRadius: "10px", fontSize: "13px", color: C.danger, fontWeight: 600 }}>
            {error}
          </div>
        )}
        {!notManager && !error && loading && <EmptyRow text="Loading…" />}

        {!notManager && !error && !loading && data && (
          <>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
              {TABS.map((t) => (
                <StatCard
                  key={t.id}
                  label={t.label}
                  value={kpis[t.id].count}
                  sub={fmtINR(kpis[t.id].amount)}
                  tone={t.tone}
                  selected={tab === t.id}
                  onClick={() => setTab(t.id)}
                />
              ))}
            </div>

            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: "12px", overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", padding: "14px 18px", borderBottom: `1px solid ${C.border}` }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: "14px", fontWeight: 800, color: C.text }}>
                    {active.title}
                    <span style={{ color: C.textSub, fontWeight: 600 }}>
                      {q && visible.length !== (dated[tab] || []).length
                        ? ` (${visible.length} of ${(dated[tab] || []).length})`
                        : ` (${visible.length})`}
                    </span>
                    <span style={{ color: C.textSub, fontWeight: 600 }}>{` · ${fmtINR(sumAmount(visible))}`}</span>
                  </div>
                  <div style={{ fontSize: "12px", color: C.textSub, marginTop: "2px" }}>{active.subtitle}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                  <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                      <SvgIcon name="search" size={14} color={C.textSub} />
                    </span>
                    <input
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search patient, invoice, admission…"
                      style={{ width: "260px", maxWidth: "100%", padding: "7px 32px 7px 32px", border: `1px solid ${C.border}`, borderRadius: "8px", fontSize: "13px", color: C.text, background: "#fff", fontFamily: "inherit" }}
                    />
                    {query ? (
                      <button
                        type="button"
                        onClick={() => setQuery("")}
                        aria-label="Clear search"
                        style={{ position: "absolute", right: "6px", top: "50%", transform: "translateY(-50%)", border: "none", background: "transparent", cursor: "pointer", padding: "2px", display: "flex" }}
                      >
                        <SvgIcon name="x" size={14} color={C.textSub} />
                      </button>
                    ) : null}
                  </div>
                  <div style={{ display: "flex", background: "#F1F5F9", borderRadius: "8px", padding: "3px" }}>
                    {DATE_PRESETS.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        className="bmd-pill"
                        onClick={() => setRange(p.id)}
                        aria-pressed={range === p.id}
                        style={{
                          padding: "5px 10px",
                          border: "none",
                          borderRadius: "6px",
                          background: range === p.id ? "#fff" : "transparent",
                          color: range === p.id ? C.text : C.textSub,
                          fontSize: "12px",
                          fontWeight: 700,
                          cursor: "pointer",
                          boxShadow: range === p.id ? "0 1px 2px rgba(15,23,42,0.08)" : "none",
                          fontFamily: "inherit",
                        }}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {q && visible.length === 0 && (dated[tab] || []).length > 0 ? (
                <EmptyRow text={`No matches for “${query.trim()}”.`} />
              ) : tab === "services" ? (
                <ServiceTable rows={visible} empty={active.empty} />
              ) : (
                <ApprovalTable rows={visible} badge={active.badge} empty={active.empty} expanded={expanded} onToggle={toggleExpanded} />
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
